import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useModelStatusesStore } from '../../stores/useModelStatusesStore';
import { ModelStatusEditor } from './ModelStatusEditor';
import styles from './ModelStatusBadge.module.css';

interface AdminStatusButtonProps {
  modelId: string;
  modelType: 'chat' | 'image' | 'video';
  modelDisplayName: string;
  isAdmin: boolean;
  compact?: boolean;
}

/**
 * AdminStatusButton — the [+] / [✎] button visible only to admins
 * Opens the ModelStatusEditor modal on click
 */
export const AdminStatusButton = ({ modelId, modelType, modelDisplayName, isAdmin, compact = false }: AdminStatusButtonProps) => {
  const { t } = useTranslation('common');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const getStatus = useModelStatusesStore(state => state.getStatus);
  const status = getStatus(modelId, modelType);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditorOpen(true);
  }, []);

  if (!isAdmin) return null;

  return (
    <>
      <button
        className={`${styles.adminStatusButton} ${status ? styles.hasStatus : ''} ${compact ? styles.compact : ''}`}
        onClick={handleClick}
        title={t('modelStatus.manage')}
        style={status ? { borderColor: `${status.bgColor}40`, color: status.textColor } : undefined}
      >
        {status ? '✎' : '+'}
      </button>
      {isEditorOpen && (
        <ModelStatusEditor
          modelId={modelId}
          modelType={modelType}
          modelDisplayName={modelDisplayName}
          onClose={() => setIsEditorOpen(false)}
        />
      )}
    </>
  );
};

export default AdminStatusButton;
